import { useState, useEffect, useRef, createElement } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLogin, { ADMIN_TOKEN_KEY, adminHeaders } from "@/components/admin/AdminLogin";
import BookingBannerControl from "@/components/admin/BookingBannerControl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatBox({ title, value, sub }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
      <p className="text-sm text-gray-500 mb-2">{title}</p>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function BarItem({ label, count, pct }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.style.width = pct + "%";
  }, [pct]);
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-500 w-16">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
        <div ref={ref} className="h-full bg-amber-500 rounded-full"></div>
      </div>
      <span className="text-sm font-bold text-slate-900 w-10 text-right">{count}</span>
    </div>
  );
}

function CTARow({ label, count }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-700">{label}</span>
      <span className="text-sm font-bold text-slate-900 bg-amber-100 px-3 py-1 rounded-full">{count}</span>
    </div>
  );
}

function ReminderRow({ r }) {
  return (
    <tr className="border-b border-gray-50">
      <td className="py-2 pr-3 text-sm text-gray-800">{r.parent_name}</td>
      <td className="py-2 pr-3 text-sm text-gray-700">{r.email}</td>
      <td className="py-2 pr-3 text-sm text-gray-700">{r.child_age}</td>
      <td className="py-2 pr-3 text-xs text-gray-600">{r.holidays.join(", ")}</td>
      <td className="py-2 text-xs text-gray-400">{r.created_at.slice(0, 10)}</td>
    </tr>
  );
}

function buildReminderElements(list) {
  var els = [];
  for (var i = 0; i < list.length; i++) {
    els.push(createElement(ReminderRow, { key: list[i].id, r: list[i] }));
  }
  return els;
}

function buildStatElements(raw) {
  var els = [];
  els.push(createElement(StatBox, { key: "pv", title: "Page Views", value: raw.overview.total_page_views, sub: raw.today.page_views + " today" }));
  els.push(createElement(StatBox, { key: "uv", title: "Unique Visitors", value: raw.overview.unique_visitors }));
  els.push(createElement(StatBox, { key: "ct", title: "CTA Clicks", value: raw.overview.total_cta_clicks, sub: raw.today.cta_clicks + " today" }));
  els.push(createElement(StatBox, { key: "ch", title: "Chats Started", value: raw.overview.total_chats_started, sub: raw.overview.total_chat_messages + " msgs" }));
  els.push(createElement(StatBox, { key: "cf", title: "Contact Forms", value: raw.overview.total_contact_submissions }));
  els.push(createElement(StatBox, { key: "rm", title: "Reminder Sign-ups", value: raw.overview.total_reminder_signups }));
  els.push(createElement(StatBox, { key: "wv", title: "7-Day Views", value: raw.week.page_views, sub: raw.week.cta_clicks + " CTA clicks" }));
  return els;
}

function buildBarElements(raw) {
  var els = [];
  var maxVal = 1;
  for (var i = 0; i < raw.daily_views.length; i++) {
    if (raw.daily_views[i].views > maxVal) maxVal = raw.daily_views[i].views;
  }
  for (var j = 0; j < raw.daily_views.length; j++) {
    var d = raw.daily_views[j];
    var pct = Math.max(Math.round((d.views / maxVal) * 100), 2);
    els.push(createElement(BarItem, { key: d.date, label: d.date.slice(5), count: d.views, pct: pct }));
  }
  return els;
}

function buildCTAElements(raw) {
  var els = [];
  for (var i = 0; i < raw.top_ctas.length; i++) {
    var c = raw.top_ctas[i];
    els.push(createElement(CTARow, { key: i, label: c.label, count: c.count }));
  }
  return els;
}

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setData(null);
    setAuthed(false);
  };

  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    fetch(BACKEND_URL + "/api/analytics/dashboard", { headers: adminHeaders() })
      .then(r => { if (r.status === 401) { logout(); return null; } return r.json(); })
      .then(d => { if (d) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
    fetch(BACKEND_URL + "/api/reminders", { headers: adminHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setReminders(d))
      .catch(() => {});
  }, [authed]);

  const downloadCsv = async () => {
    const res = await fetch(BACKEND_URL + "/api/reminders/csv", { headers: adminHeaders() });
    if (res.status === 401) return logout();
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = "holiday-reminders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Unable to load analytics.</p>
      </div>
    );
  }

  var statEls = buildStatElements(data);
  var barEls = buildBarElements(data);
  var ctaEls = buildCTAElements(data);
  var reminderEls = buildReminderElements(reminders);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="analytics-dashboard">
      <div className="bg-slate-900 text-white py-6 px-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-white/70 hover:text-white" data-testid="analytics-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold flex-1">Analytics Dashboard</h1>
          <button onClick={logout} className="text-sm text-white/70 hover:text-white border border-white/30 px-3 py-1.5 rounded" data-testid="admin-logout-btn">
            Log out
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <BookingBannerControl onUnauthorized={logout} />

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {statEls}
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Daily Views (Last 7 Days)</h2>
          {barEls.length === 0 ? (
            <p className="text-gray-400 text-sm py-6 text-center">No data yet.</p>
          ) : (
            <div className="space-y-2">{barEls}</div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Top CTA Clicks</h2>
          {ctaEls.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">No clicks tracked yet.</p>
          ) : (
            <div className="space-y-3">{ctaEls}</div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-testid="reminders-panel">
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <h2 className="text-lg font-bold text-slate-900">Holiday Reminder Sign-ups</h2>
            <button onClick={downloadCsv} className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-4 py-2 rounded" data-testid="reminders-csv-download">
              Download CSV
            </button>
          </div>
          {reminderEls.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">No sign-ups yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="reminders-table">
                <thead>
                  <tr className="text-xs uppercase text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-3">Parent</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Age</th><th className="py-2 pr-3">Holidays</th><th className="py-2">Signed up</th>
                  </tr>
                </thead>
                <tbody>{reminderEls}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
